import { useState, useEffect } from 'react';
import { LoadingPage } from '@/components/common/Loading';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Settings, RotateCcw, Save, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = '';

export default function ConfigManagement() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = () => {
    setLoading(true);
    fetch(`${API_BASE}/api/admin/config`, { headers })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (key) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/config`, {
        method: 'POST', headers, body: JSON.stringify({ key, value: parseFloat(editValue) }),
      });
      if (res.ok) { toast.success('Config updated'); setEditingKey(null); load(); }
      else toast.error('Update failed');
    } catch { toast.error('Update failed'); }
  };

  const handleReset = async (key) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/config/reset/${key}`, { method: 'POST', headers });
      if (res.ok) { toast.success('Config reset to default'); load(); }
      else toast.error('Reset failed');
    } catch { toast.error('Reset failed'); }
  };

  if (loading) return <LoadingPage />;

  const CATEGORY_LABELS = {
    thresholds: 'Thresholds',
    cache: 'Cache TTL',
    scheduler: 'Scheduler',
    data_quality: 'Data Quality',
    provider: 'Provider',
    premium: 'Premium',
  };

  return (
    <div data-testid="config-management-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight uppercase text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
          <Settings className="w-5 h-5 inline mr-2 text-violet-400" />
          Config Management
        </h1>
        <p className="text-sm text-slate-500 mt-1">System-wide configuration values</p>
      </div>

      {data?.categories && Object.entries(data.categories).map(([cat, items]) => (
        <div key={cat} className="mb-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
            {CATEGORY_LABELS[cat] || cat}
          </h2>
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/80">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase">Setting</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-400 uppercase w-[120px]">Value</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-400 uppercase w-[100px]">Default</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-400 uppercase w-[120px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(cfg => {
                  const isModified = cfg.updated_at !== null && cfg.value !== cfg.default_value;
                  return (
                    <tr key={cfg.key} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`config-row-${cfg.key}`}>
                      <td className="px-4 py-3">
                        <p className="text-slate-200 font-medium text-sm">{cfg.label}</p>
                        <p className="text-[10px] text-slate-600 mt-0.5">{cfg.description}</p>
                      </td>
                      <td className="px-3 py-3 text-center">
                        {editingKey === cfg.key ? (
                          <Input type="number" value={editValue} onChange={e => setEditValue(e.target.value)}
                            className="bg-slate-800 border-slate-700 text-xs h-7 w-20 mx-auto text-center" />
                        ) : (
                          <span className={`tabular-nums font-medium ${isModified ? 'text-cyan-400' : 'text-slate-300'}`}>
                            {cfg.value}
                          </span>
                        )}
                        {isModified && editingKey !== cfg.key && (
                          <Badge className="ml-1 text-[9px] bg-cyan-500/10 text-cyan-400 border-cyan-500/20">modified</Badge>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center text-xs text-slate-500 tabular-nums">{cfg.default_value}</td>
                      <td className="px-3 py-3 text-center">
                        {editingKey === cfg.key ? (
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" onClick={() => handleSave(cfg.key)}
                              className="bg-emerald-500/10 text-emerald-400 h-7 w-7 p-0">
                              <Check className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" onClick={() => setEditingKey(null)}
                              className="bg-slate-700 text-slate-400 h-7 text-xs px-2">Cancel</Button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" variant="ghost" onClick={() => { setEditingKey(cfg.key); setEditValue(String(cfg.value)); }}
                              className="text-slate-500 hover:text-slate-300 h-7 text-xs"
                              data-testid={`edit-config-${cfg.key}`}>
                              Edit
                            </Button>
                            {isModified && (
                              <Button size="sm" variant="ghost" onClick={() => handleReset(cfg.key)}
                                className="text-slate-500 hover:text-amber-400 h-7 w-7 p-0" title="Reset to default">
                                <RotateCcw className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
